// Live quotes, the watchlist, the deployment signal and the news feed.
// These poll, so they carry a refresh interval.

import type { DeploymentSignal } from "@/finance/reporting/model/signal.js";

import type { MarketDataSource, WatchlistEntry } from "@/finance/model/wire.js";
import { api, kept, polled, qk } from "@/platform/client";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const MARKET_REFRESH_MS = 60_000;

// ---- markets ----
export const watchlistQuery = queryOptions({
  queryKey: qk.watchlist.all,
  queryFn: () => api.get<WatchlistEntry[]>("/market/watchlist"),
  ...polled(MARKET_REFRESH_MS),
  ...kept,
});

export const useWatchlist = () => useQuery(watchlistQuery);

/** A watchlist change changes which names the news follows. */
function invalidateWatchlist(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: qk.watchlist.all });
  qc.invalidateQueries({ queryKey: qk.marketNews.all });
}

export function useAddWatchlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { symbol: string; label?: string }) => api.post("/market/watchlist", input),
    onSuccess: () => invalidateWatchlist(qc),
  });
}

export function useRemoveWatchlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/market/watchlist/${id}`),
    onSuccess: () => invalidateWatchlist(qc),
  });
}

export function useReorderWatchlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => api.post("/market/watchlist/reorder", { ids }),
    onMutate: async (ids: number[]) => {
      // optimistic: reorder the cached list immediately
      await qc.cancelQueries({ queryKey: qk.watchlist.all });
      const prev = qc.getQueryData<WatchlistEntry[]>(["watchlist"]);
      if (prev) {
        const byId = new Map(prev.map((e) => [e.id, e]));
        qc.setQueryData(
          ["watchlist"],
          ids.map((id) => byId.get(id)).filter(Boolean) as WatchlistEntry[],
        );
      }
      return { prev };
    },
    onError: (_e, _ids, ctx) => {
      if (ctx?.prev) qc.setQueryData(["watchlist"], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: qk.watchlist.all }),
  });
}

/** Whether market data answers, and which provider does. */
export const marketSourceQuery = queryOptions({
  queryKey: qk.marketSource.all,
  queryFn: () => api.get<MarketDataSource>("/market/source"),
  ...kept,
});

export const useMarketSource = () => useQuery(marketSourceQuery);

export const deploymentSignalQuery = queryOptions({
  queryKey: qk.marketSignal.all,
  queryFn: () => api.get<DeploymentSignal>("/market/signal"),
  ...polled(MARKET_REFRESH_MS),
  ...kept,
});

export const useDeploymentSignal = () => useQuery(deploymentSignalQuery);
